import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, NgZone } from "@angular/core";
import {
  CDN_ASSET_BASE_URL,
  isCdnAssetUrl,
  isLocalAssetFallbackEnabled,
  toLocalAssetUrl
} from "../utils/asset-url.util";

const RESOURCE_ATTRIBUTES = ["src", "href", "poster", "xlink:href"];
const CSS_URL_PATTERN = /url\(\s*(?:"([^"]+)"|'([^']+)'|([^\s)]+))\s*\)/g;
const CSS_ASSET_TIMEOUT_MS = 10_000;
// Browsers fetch mask images in CORS mode, unlike background images.
const CORS_CSS_PROPERTY_PATTERN = /^(?:-webkit-)?mask/;

@Injectable({ providedIn: "root" })
export class AssetFallbackService {
  private readonly cssAssetChecks = new Map<string, Promise<boolean>>();
  private observer?: MutationObserver;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ngZone: NgZone
  ) {}

  initialize(): void {
    const view = this.document.defaultView;
    if (
      !view ||
      this.observer ||
      !isLocalAssetFallbackEnabled(this.document.location.hostname)
    ) {
      return;
    }

    this.document.addEventListener("error", this.handleResourceError, true);

    this.ngZone.runOutsideAngular(() => {
      this.scanStyleRules();
      view.addEventListener("load", () => this.scanStyleRules(), {
        once: true
      });

      this.observer = new view.MutationObserver((records) => {
        for (const record of records) {
          if (record.type === "attributes") {
            this.checkInlineStyle(record.target);
            continue;
          }

          record.addedNodes.forEach((node) => this.checkAddedNode(node));
        }
      });
      this.observer.observe(this.document.documentElement, {
        attributes: true,
        attributeFilter: ["style"],
        childList: true,
        subtree: true
      });
    });
  }

  private readonly handleResourceError = (event: Event): void => {
    const view = this.document.defaultView;
    if (!view || !(event.target instanceof view.Element)) {
      return;
    }

    const element = event.target;
    for (const attribute of RESOURCE_ATTRIBUTES) {
      const value = element.getAttribute(attribute);
      if (value && isCdnAssetUrl(value)) {
        element.setAttribute(attribute, toLocalAssetUrl(value));
      }
    }
  };

  private checkAddedNode(node: Node): void {
    const view = this.document.defaultView;
    if (!view || !(node instanceof view.Element)) {
      return;
    }

    this.checkInlineStyle(node);
    node
      .querySelectorAll<HTMLElement>("[style]")
      .forEach((element) => this.checkInlineStyle(element));

    if (node.localName === "style") {
      queueMicrotask(() => this.scanStyleRules());
    }
  }

  private checkInlineStyle(target: EventTarget): void {
    const view = this.document.defaultView;
    if (!view || !(target instanceof view.HTMLElement)) {
      return;
    }

    this.checkCssDeclaration(target.style);
  }

  private scanStyleRules(): void {
    Array.from(this.document.styleSheets).forEach((styleSheet) => {
      try {
        this.scanCssRules(styleSheet.cssRules);
      } catch {
        // Cross-origin stylesheets do not expose cssRules and contain no app assets.
      }
    });
  }

  private scanCssRules(rules: CSSRuleList): void {
    Array.from(rules).forEach((rule) => {
      const style = (rule as CSSStyleRule).style;
      if (style?.cssText.includes(CDN_ASSET_BASE_URL)) {
        this.checkCssDeclaration(style);
      }

      const nestedRules = (rule as CSSGroupingRule).cssRules;
      if (nestedRules) {
        this.scanCssRules(nestedRules);
      }
    });
  }

  private checkCssDeclaration(style: CSSStyleDeclaration): void {
    Array.from(style).forEach((property) =>
      this.checkCssProperty(style, property)
    );
  }

  private checkCssProperty(style: CSSStyleDeclaration, property: string): void {
    const value = style.getPropertyValue(property);
    const cdnUrls = this.extractCdnUrls(value);
    const requiresCors = CORS_CSS_PROPERTY_PATTERN.test(property);

    cdnUrls.forEach((cdnUrl) => {
      void this.isAssetAvailable(cdnUrl, requiresCors).then((isAvailable) => {
        if (isAvailable) {
          return;
        }

        const currentValue = style.getPropertyValue(property);
        if (!currentValue.includes(cdnUrl)) {
          return;
        }

        style.setProperty(
          property,
          currentValue.split(cdnUrl).join(toLocalAssetUrl(cdnUrl)),
          style.getPropertyPriority(property)
        );
      });
    });
  }

  private extractCdnUrls(value: string): string[] {
    const urls: string[] = [];
    for (const match of value.matchAll(CSS_URL_PATTERN)) {
      const url = match[1] ?? match[2] ?? match[3];
      if (url && isCdnAssetUrl(url)) {
        urls.push(url);
      }
    }
    return urls;
  }

  private isAssetAvailable(
    url: string,
    requiresCors: boolean
  ): Promise<boolean> {
    const cacheKey = `${requiresCors ? "cors" : "no-cors"}:${url}`;
    const cachedCheck = this.cssAssetChecks.get(cacheKey);
    if (cachedCheck) {
      return cachedCheck;
    }

    const check = new Promise<boolean>((resolve) => {
      const view = this.document.defaultView;
      if (!view) {
        resolve(false);
        return;
      }

      const image = new view.Image();
      let settled = false;
      const finish = (available: boolean): void => {
        if (settled) {
          return;
        }
        settled = true;
        view.clearTimeout(timeoutId);
        resolve(available);
      };
      const timeoutId = view.setTimeout(
        () => finish(false),
        CSS_ASSET_TIMEOUT_MS
      );
      image.onload = () => finish(true);
      image.onerror = () => finish(false);
      if (requiresCors) {
        image.crossOrigin = "anonymous";
      }
      image.src = url;
    });

    this.cssAssetChecks.set(cacheKey, check);
    return check;
  }
}
